import React from 'react';
import Helpers from '@Apis/helpers';
import { QUANTITY } from '@Apis/constants';
import FormHelpers from '../../../../helpers/FormHelpers';
import { message } from 'antd';
import { ERROR } from '../../../../actions/base';

// Constants for error messages
const ERROR_MESSAGES = {
  requiredField: (fieldName) => `Error in ${fieldName} field. Select/Enter a valid value then proceed.`,
  consumptionQuantityExceeded: (productName) => `Error in Consumption Quantity for Product ${productName} field. Entered quantity is more than available quantity.`,
};

// Utility to calculate available quantity
const calculateAvailableQuantity = (item, allStock) => {
  if (item?.bundle_products?.length) {
    const availableQuantities = item.bundle_products.map((bundleProduct) => {
      const availableQty = Helpers.getValueTotalInObject(
        bundleProduct?.product_batches?.filter(
          (batch) => batch?.batch_in_use && (allStock ? !batch?.is_rejected_batch : true),
        ),
        'available_qty',
      );
      return availableQty / (bundleProduct.quantity / (item?.quantity || 1));
    });
    return Math.min(...availableQuantities);
  }

  return Helpers.getValueTotalInObject(
    item?.product_batches?.filter(
      (batch) => batch?.batch_in_use && (allStock ? !batch?.is_rejected_batch : true),
    ),
    'available_qty',
  );
};

// Utility to validate document-level errors
const validateDocLevel = ({
  selectedCustomer,
  location,
  tenantDepartmentId,
  orderDate,
  cfConsumptionOrderDoc,
  shipFromAddress,
  shipToAddress,
}) => {
  const errors = [];

  errors.push({
    name: 'Customer',
    value: selectedCustomer,
    message: ERROR_MESSAGES.requiredField('Customer'),
  });

  errors.push({
    name: 'Department',
    value: tenantDepartmentId,
    message: ERROR_MESSAGES.requiredField('Department'),
  });

  errors.push({
    name: 'Order Date',
    value: orderDate,
    message: ERROR_MESSAGES.requiredField('Order Date'),
  });

  if (cfConsumptionOrderDoc) {
    cfConsumptionOrderDoc.forEach((field) => {
      if (field?.isRequired && field?.isActive) {
        errors.push({
          name: field?.fieldName,
          value: field?.fieldValue,
          message: ERROR_MESSAGES.requiredField(field?.fieldName),
        });
      }
    });
  }

  errors.push({
    name: 'Ship To Address',
    value: shipToAddress,
    message: ERROR_MESSAGES.requiredField('Ship To Address'),
  });

  errors.push({
    name: 'Ship From Address',
    value: shipFromAddress,
    message: ERROR_MESSAGES.requiredField('Ship From Address'),
  });

  return errors;
};

// Utility to validate line-level errors
const validateLineLevel = ({ data, tenantDepartmentId, allStock }) => {
  const errors = [];

  if (data && tenantDepartmentId) {
    data.forEach((item) => {
      if (!item?.product_sku_info) {
        errors.push({
          name: 'Product',
          value: 0,
          message: 'Error in Product column. Select a product to continue.',
        });
      } else {
        const availableQty = calculateAvailableQuantity(item, allStock) || 0;
        const normalizedQty = QUANTITY(
          (availableQty / item?.uom_info?.ratio)
          * item?.product_sku_info?.uom_info?.ratio,
          item?.uom_info?.precision || 0,
        );

        errors.push({
          name: `Consumption Quantity for Product ${item?.product_sku_info?.internal_sku_code || ''
            }`,
          value:
            Number(item?.consumption_quantity) > normalizedQty
              ? 0
              : Number(item?.consumption_quantity),
          message:
            Number(item?.consumption_quantity) > normalizedQty
              ? ERROR_MESSAGES.consumptionQuantityExceeded(
                item?.product_sku_info?.internal_sku_code || '',
              )
              : ERROR_MESSAGES.requiredField(
                `Consumption Quantity for Product ${item?.product_sku_info?.internal_sku_code || ''
                }`,
              ),
        });

        item?.lineCustomFields?.forEach((customField) => {
          if (customField?.isRequired && customField?.isActive) {
            errors.push({
              name: customField?.fieldName,
              value: customField?.fieldValue,
              message: `Error in ${customField?.fieldName} of product ${item?.product_sku_info?.internal_sku_code}. Enter a value then proceed.`,
            });
          }
        });
      }
    });
  }

  return errors;
};

// Main function to get packing slip error list
const getConsumptionErrorList = (params) => {
  const docLevelErrors = validateDocLevel(params);
  const lineLevelErrors = validateLineLevel(params);

  return FormHelpers.errorChecker({ docLevelErrors, lineLevelErrors });
};

export default getConsumptionErrorList;
